import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addHours, parseISO, isAfter, differenceInHours } from 'date-fns';
import { Car, Calendar, MapPin, Star, Clock, ChevronRight, Search, Home, AlertTriangle, Edit3, Plus, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Navbar } from '@/components/Navbar';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';

const DriverDashboard = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [reviewDialog, setReviewDialog] = useState({ open: false, booking: null });
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [disputeDialog, setDisputeDialog] = useState({ open: false, booking: null });
  const [disputeData, setDisputeData] = useState({ category: '', description: '' });
  const [submittingDispute, setSubmittingDispute] = useState(false);
  const [disputeCategories, setDisputeCategories] = useState({});
  
  // New state for modify/extend booking
  const [modifyDialog, setModifyDialog] = useState({ open: false, booking: null });
  const [modifyData, setModifyData] = useState({ startDate: null, endDate: null, startTime: '09:00', endTime: '17:00' });
  const [submittingModify, setSubmittingModify] = useState(false);
  const [extendDialog, setExtendDialog] = useState({ open: false, booking: null });
  const [extendHours, setExtendHours] = useState(1);
  const [submittingExtend, setSubmittingExtend] = useState(false);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        const [bookingsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/bookings`, { withCredentials: true, headers }),
          axios.get(`${API}/disputes/categories`)
        ]);
        console.log('Fetched bookings:', bookingsRes.data?.length, bookingsRes.data);
        setBookings(bookingsRes.data || []);
        setDisputeCategories(categoriesRes.data.categories || {});
      } catch (error) {
        console.error('Error fetching bookings:', error);
        // Show empty array instead of failing
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFileDispute = async () => {
    if (!disputeData.category) {
      toast.error('Please select a category');
      return;
    }
    
    setSubmittingDispute(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/disputes`, {
        booking_id: disputeDialog.booking.booking_id,
        category: disputeData.category,
        description: disputeData.description
      }, { withCredentials: true, headers });
      
      toast.success('Dispute filed successfully. We\'ll review within 24-48 hours.');
      setDisputeDialog({ open: false, booking: null });
      setDisputeData({ category: '', description: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to file dispute');
    } finally {
      setSubmittingDispute(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;

    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/bookings/${bookingId}/status?status=cancelled`, {}, { withCredentials: true, headers });
      toast.success('Booking cancelled');
      const res = await axios.get(`${API}/bookings`, { withCredentials: true, headers });
      setBookings(res.data);
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const handleCompletePayment = async (bookingId) => {
    try {
      const headers = getAuthHeaders();
      const paymentRes = await axios.post(`${API}/payments/stripe/create-session`, {
        booking_id: bookingId,
        payment_method: 'stripe',
        origin_url: window.location.origin
      }, { withCredentials: true, headers });
      
      window.location.href = paymentRes.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment session');
    }
  };

  const handleCheckout = async (bookingId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/bookings/${bookingId}/checkout`, {}, { withCredentials: true, headers });
      toast.success("Marked as checked out — no further charges.");
      // Optimistically update local state
      setBookings(prev => prev.map(b =>
        b.booking_id === bookingId
          ? { ...b, checked_out_at: res.data.checked_out_at, checked_out_by: res.data.checked_out_by }
          : b
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark checkout");
    }
  };

  const handleBecomeHost = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.put(`${API}/auth/role`, { role: 'host' }, { 
        withCredentials: true,
        headers 
      });
      toast.success(res.data.is_early_adopter ? 'Congratulations! You\'re an Early Host!' : 'You\'re now a host!');
      setUser({ ...user, role: 'host', is_early_adopter: res.data.is_early_adopter });
      navigate('/host');
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewData.comment.trim()) {
      toast.error('Please write a review');
      return;
    }

    setSubmittingReview(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/reviews`, {
        booking_id: reviewDialog.booking.booking_id,
        rating: reviewData.rating,
        comment: reviewData.comment,
        review_type: 'driver_to_host'
      }, { withCredentials: true, headers });
      
      toast.success('Review submitted!');
      setReviewDialog({ open: false, booking: null });
      setReviewData({ rating: 5, comment: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Check if booking can be modified (2+ hours before start)
  const canModifyBooking = (booking) => {
    if (!['confirmed', 'pending_payment'].includes(booking.status)) return false;
    const startTime = parseISO(booking.start_time);
    const now = new Date();
    return differenceInHours(startTime, now) >= 2;
  };

  // Check if booking is currently active (can be extended)
  const isBookingActive = (booking) => {
    if (booking.status !== 'confirmed') return false;
    const startTime = parseISO(booking.start_time);
    const endTime = parseISO(booking.end_time);
    const now = new Date();
    return isAfter(now, startTime) && isAfter(endTime, now);
  };

  // Open modify dialog with booking data
  const openModifyDialog = (booking) => {
    const startTime = parseISO(booking.start_time);
    const endTime = parseISO(booking.end_time);
    setModifyData({
      startDate: startTime,
      endDate: endTime,
      startTime: format(startTime, 'HH:mm'),
      endTime: format(endTime, 'HH:mm')
    });
    setModifyDialog({ open: true, booking });
  };

  // Handle modify booking submission
  const handleModifyBooking = async () => {
    if (!modifyData.startDate || !modifyData.endDate) {
      toast.error('Please select dates');
      return;
    }

    setSubmittingModify(true);
    try {
      const headers = getAuthHeaders();
      const start = new Date(modifyData.startDate);
      start.setHours(parseInt(modifyData.startTime.split(':')[0]), parseInt(modifyData.startTime.split(':')[1]));
      
      const end = new Date(modifyData.endDate);
      end.setHours(parseInt(modifyData.endTime.split(':')[0]), parseInt(modifyData.endTime.split(':')[1]));

      const res = await axios.put(`${API}/bookings/${modifyDialog.booking.booking_id}/modify`, {
        start_time: start.toISOString(),
        end_time: end.toISOString()
      }, { withCredentials: true, headers });
      
      toast.success('Booking modified successfully!');
      setModifyDialog({ open: false, booking: null });
      
      // Refresh bookings
      const bookingsRes = await axios.get(`${API}/bookings`, { withCredentials: true, headers });
      setBookings(bookingsRes.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.suggested_slots) {
        toast.error(detail.message);
        // Could show suggested slots here
      } else {
        toast.error(detail || 'Failed to modify booking');
      }
    } finally {
      setSubmittingModify(false);
    }
  };

  // Handle extend booking
  const handleExtendBooking = async () => {
    if (extendHours < 0.5) {
      toast.error('Minimum extension is 30 minutes');
      return;
    }

    setSubmittingExtend(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/bookings/${extendDialog.booking.booking_id}/extend`, {
        additional_hours: extendHours
      }, { withCredentials: true, headers });
      
      toast.success(`Booking extended by ${extendHours} hour(s)! Additional cost: $${res.data.extension_details.additional_cost}`);
      setExtendDialog({ open: false, booking: null });
      setExtendHours(1);
      
      // Refresh bookings
      const bookingsRes = await axios.get(`${API}/bookings`, { withCredentials: true, headers });
      setBookings(bookingsRes.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.max_extension_hours !== undefined) {
        toast.error(`Cannot extend that long. Maximum extension: ${detail.max_extension_hours.toFixed(1)} hours`);
      } else {
        toast.error(detail || 'Failed to extend booking');
      }
    } finally {
      setSubmittingExtend(false);
    }
  };

  // Time options for modify dialog
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      timeOptions.push(time);
    }
  }

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      pending_payment: 'bg-orange-100 text-orange-700',
      confirmed: 'bg-green-100 text-green-700',
      paid: 'bg-green-100 text-green-700',  // Alias for confirmed
      completed: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700',
      expired: 'bg-gray-100 text-gray-500'
    };
    const labels = {
      pending: 'Pending',
      pending_payment: 'Awaiting Payment',
      confirmed: 'Confirmed',
      paid: 'Paid & Confirmed',  // Alias for confirmed
      completed: 'Completed',
      cancelled: 'Cancelled',
      expired: 'Expired'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{labels[status] || status}</Badge>;
  };

  // Include 'paid' as an alias for confirmed
  const upcomingBookings = bookings.filter(b => ['pending', 'pending_payment', 'confirmed', 'paid'].includes(b.status));
  const pastBookings = bookings.filter(b => ['completed', 'cancelled', 'expired'].includes(b.status));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <div className="pt-20 flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-[#5ebc67] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      <div className="pt-24 pb-12">
        <div className="container-main">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                My Bookings
              </h1>
              <p className="text-slate-600">Welcome back, {user?.name}</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/search')}
                className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full"
                data-testid="find-parking-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Find Parking
              </Button>
              {user?.role === 'driver' && (
                <Button
                  onClick={handleBecomeHost}
                  variant="outline"
                  className="rounded-full"
                  data-testid="become-host-btn"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Become a Host
                </Button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{upcomingBookings.length}</p>
                    <p className="text-sm text-slate-500">Upcoming</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Car className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {pastBookings.filter(b => b.status === 'completed').length}
                    </p>
                    <p className="text-sm text-slate-500">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Star className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      ${bookings.reduce((sum, b) => sum + (b.status === 'completed' ? b.total_price : 0), 0).toFixed(0)}
                    </p>
                    <p className="text-sm text-slate-500">Total Spent</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="upcoming" data-testid="tab-upcoming">
                Upcoming ({upcomingBookings.length})
              </TabsTrigger>
              <TabsTrigger value="past" data-testid="tab-past">
                Past ({pastBookings.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming">
              {upcomingBookings.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No upcoming bookings</h3>
                    <p className="text-slate-500 mb-6">Find your next parking spot</p>
                    <Button onClick={() => navigate('/search')} className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full">
                      <Search className="w-4 h-4 mr-2" />
                      Search Parking
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {upcomingBookings.map(booking => (
                    <Card key={booking.booking_id} className="overflow-hidden" data-testid={`booking-${booking.booking_id}`}>
                      <CardContent className="p-0">
                        <div className="flex flex-col md:flex-row">
                          <div className="md:w-48 h-32 md:h-auto">
                            <img
                              src={booking.listing?.photos?.[0] || 'https://images.unsplash.com/photo-1571799923032-df849d54bcb9?w=300'}
                              alt={booking.listing?.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 p-4 md:p-6">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-slate-900">{booking.listing?.title}</h3>
                                  {getStatusBadge(booking.status)}
                                </div>
                                <div className="flex items-center gap-1 text-sm text-slate-500 mb-2">
                                  <MapPin className="w-4 h-4" />
                                  {booking.listing?.address}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-slate-600">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {format(new Date(booking.start_time), 'MMM d, yyyy')}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {format(new Date(booking.start_time), 'h:mm a')} - {format(new Date(booking.end_time), 'h:mm a')}
                                  </div>
                                </div>
                                {/* Active booking indicator */}
                                {isBookingActive(booking) && (
                                  <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                    <Timer className="w-3 h-3 animate-pulse" />
                                    Currently Active
                                  </div>
                                )}
                                {/* Overstay running total */}
                                {(booking.overstay_total || 0) > 0 && (
                                  <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold" data-testid={`overstay-badge-${booking.booking_id}`}>
                                    <AlertTriangle className="w-3 h-3" />
                                    Overstay charged: ${booking.overstay_total.toFixed(2)}
                                  </div>
                                )}
                                {booking.checked_out_at && (
                                  <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs">
                                    Checked out by {booking.checked_out_by || 'you'}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-slate-900">${booking.total_price?.toFixed(2) || '0.00'}</p>
                                <div className="flex gap-2 mt-2 flex-wrap justify-end">
                                  <Button size="sm" variant="outline" onClick={() => navigate(`/listing/${booking.listing_id}`)}>
                                    View
                                  </Button>
                                  {booking.status === 'pending_payment' && (
                                    <Button 
                                      size="sm" 
                                      className="bg-[#5ebc67] hover:bg-[#4da855]"
                                      onClick={() => handleCompletePayment(booking.booking_id)}
                                      data-testid={`pay-now-${booking.booking_id}`}
                                    >
                                      Pay Now
                                    </Button>
                                  )}
                                  {/* "I've left" / Check-out button — visible while booking is active or recently ended, until they tap it */}
                                  {booking.status === 'confirmed' && !booking.checked_out_at && (
                                    <Button
                                      size="sm"
                                      className="bg-[#5ebc67] hover:bg-[#4da855]"
                                      onClick={() => handleCheckout(booking.booking_id)}
                                      data-testid={`checkout-${booking.booking_id}`}
                                    >
                                      I've left
                                    </Button>
                                  )}
                                  {/* Modify booking button - only if 2+ hours before start */}
                                  {canModifyBooking(booking) && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="text-blue-600"
                                      onClick={() => openModifyDialog(booking)}
                                      data-testid={`modify-${booking.booking_id}`}
                                    >
                                      <Edit3 className="w-3 h-3 mr-1" />
                                      Modify
                                    </Button>
                                  )}
                                  {/* Extend booking button - only for active bookings */}
                                  {isBookingActive(booking) && (
                                    <Button 
                                      size="sm" 
                                      className="bg-blue-600 hover:bg-blue-700"
                                      onClick={() => setExtendDialog({ open: true, booking })}
                                      data-testid={`extend-${booking.booking_id}`}
                                    >
                                      <Plus className="w-3 h-3 mr-1" />
                                      Extend
                                    </Button>
                                  )}
                                  {(booking.status === 'pending' || booking.status === 'pending_payment') && (
                                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleCancelBooking(booking.booking_id)}>
                                      Cancel
                                    </Button>
                                  )}
                                  {(booking.status === 'confirmed' || booking.status === 'completed') && (
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="text-amber-600"
                                      onClick={() => setDisputeDialog({ open: true, booking })}
                                    >
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      Report
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="past">
              {pastBookings.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No past bookings</h3>
                    <p className="text-slate-500">Your booking history will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {pastBookings.map(booking => (
                    <Card key={booking.booking_id} className={`overflow-hidden ${booking.status === 'cancelled' ? 'opacity-60' : ''}`}>
                      <CardContent className="p-0">
                        <div className="flex flex-col md:flex-row">
                          <div className="md:w-48 h-32 md:h-auto">
                            <img
                              src={booking.listing?.photos?.[0] || 'https://images.unsplash.com/photo-1571799923032-df849d54bcb9?w=300'}
                              alt={booking.listing?.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 p-4 md:p-6">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-slate-900">{booking.listing?.title}</h3>
                                  {getStatusBadge(booking.status)}
                                </div>
                                <div className="flex items-center gap-1 text-sm text-slate-500 mb-2">
                                  <MapPin className="w-4 h-4" />
                                  {booking.listing?.address}
                                </div>
                                <p className="text-sm text-slate-600">
                                  {format(new Date(booking.start_time), 'MMM d, yyyy h:mm a')}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-slate-900">${booking.total_price.toFixed(2)}</p>
                                {booking.status === 'completed' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="mt-2"
                                    onClick={() => setReviewDialog({ open: true, booking })}
                                    data-testid={`review-btn-${booking.booking_id}`}
                                  >
                                    <Star className="w-4 h-4 mr-1" />
                                    Leave Review
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => setReviewDialog({ open, booking: reviewDialog.booking })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave a Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewData(d => ({ ...d, rating: star }))}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${star <= reviewData.rating ? 'star-filled' : 'text-slate-300'}`}
                      fill={star <= reviewData.rating ? 'currentColor' : 'none'}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Your Review</Label>
              <Textarea
                value={reviewData.comment}
                onChange={(e) => setReviewData(d => ({ ...d, comment: e.target.value }))}
                placeholder="Share your experience with this parking spot..."
                className="mt-2"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog({ open: false, booking: null })}>
              Cancel
            </Button>
            <Button onClick={handleSubmitReview} disabled={submittingReview} className="bg-[#5ebc67] hover:bg-[#4da855]">
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Dialog */}
      <Dialog open={disputeDialog.open} onOpenChange={(open) => setDisputeDialog({ open, booking: disputeDialog.booking })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Report a Problem
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Booking:</strong> {disputeDialog.booking?.listing?.title}
              </p>
              <p className="text-xs text-slate-500">
                {disputeDialog.booking?.start_time && format(new Date(disputeDialog.booking.start_time), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
            
            <div>
              <Label>What went wrong?</Label>
              <Select value={disputeData.category} onValueChange={(val) => setDisputeData(d => ({ ...d, category: val }))}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(disputeCategories).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>
                      <div>
                        <span className="font-medium">{cat.label}</span>
                        <span className="text-xs text-slate-500 ml-2">
                          {cat.severity === 'high' || cat.severity === 'critical' ? '(Full refund eligible)' : ''}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {disputeData.category && disputeCategories[disputeData.category] && (
                <p className="text-xs text-slate-500 mt-1">
                  {disputeCategories[disputeData.category].description}
                </p>
              )}
            </div>
            
            <div>
              <Label>Describe what happened</Label>
              <Textarea
                value={disputeData.description}
                onChange={(e) => setDisputeData(d => ({ ...d, description: e.target.value }))}
                placeholder="Please provide details about the issue you experienced..."
                className="mt-2"
                rows={4}
              />
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <strong>What happens next?</strong>
              <ul className="mt-1 list-disc list-inside text-xs">
                <li>Our team will review your report within 24-48 hours</li>
                <li>We may contact you or the host for more information</li>
                <li>Eligible refunds are processed automatically</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeDialog({ open: false, booking: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleFileDispute} 
              disabled={submittingDispute || !disputeData.category} 
              className="bg-amber-500 hover:bg-amber-600"
            >
              {submittingDispute ? 'Submitting...' : 'Submit Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modify Booking Dialog */}
      <Dialog open={modifyDialog.open} onOpenChange={(open) => setModifyDialog({ open, booking: modifyDialog.booking })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-blue-500" />
              Modify Booking
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Spot:</strong> {modifyDialog.booking?.listing?.title}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Current: {modifyDialog.booking?.start_time && format(new Date(modifyDialog.booking.start_time), 'MMM d, yyyy h:mm a')} - 
                {modifyDialog.booking?.end_time && format(new Date(modifyDialog.booking.end_time), 'h:mm a')}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>New Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {modifyData.startDate ? format(modifyData.startDate, 'MMM d, yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={modifyData.startDate}
                      onSelect={(date) => setModifyData(d => ({ ...d, startDate: date, endDate: date }))}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>New End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {modifyData.endDate ? format(modifyData.endDate, 'MMM d, yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={modifyData.endDate}
                      onSelect={(date) => setModifyData(d => ({ ...d, endDate: date }))}
                      disabled={(date) => date < (modifyData.startDate || new Date())}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Start Time
                </Label>
                <Select value={modifyData.startTime} onValueChange={(val) => setModifyData(d => ({ ...d, startTime: val }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  End Time
                </Label>
                <Select value={modifyData.endTime} onValueChange={(val) => setModifyData(d => ({ ...d, endTime: val }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <strong>Note:</strong> Price will be recalculated based on new times. 
              You can only modify bookings at least 2 hours before the start time.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModifyDialog({ open: false, booking: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleModifyBooking} 
              disabled={submittingModify || !modifyData.startDate || !modifyData.endDate} 
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submittingModify ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Booking Dialog */}
      <Dialog open={extendDialog.open} onOpenChange={(open) => setExtendDialog({ open, booking: extendDialog.booking })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-500" />
              Extend Your Booking
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Spot:</strong> {extendDialog.booking?.listing?.title}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Current end time: {extendDialog.booking?.end_time && format(new Date(extendDialog.booking.end_time), 'h:mm a')}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Add more time</Label>
              <Select value={extendHours.toString()} onValueChange={(val) => setExtendHours(parseFloat(val))}>
                <SelectTrigger data-testid="extend-hours-select">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.5">30 minutes</SelectItem>
                  <SelectItem value="1">1 hour</SelectItem>
                  <SelectItem value="2">2 hours</SelectItem>
                  <SelectItem value="3">3 hours</SelectItem>
                  <SelectItem value="4">4 hours</SelectItem>
                  <SelectItem value="6">6 hours</SelectItem>
                  <SelectItem value="8">8 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {extendDialog.booking?.listing?.price_hourly && (
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Additional cost:</span>
                  <span className="font-semibold text-green-700">
                    ${(extendDialog.booking.listing.price_hourly * extendHours * 1.15).toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  New end time: {extendDialog.booking?.end_time && format(addHours(new Date(extendDialog.booking.end_time), extendHours), 'h:mm a')}
                </p>
              </div>
            )}
            
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
              <strong>Quick tip:</strong> Extend your booking anytime while parking. 
              Payment will be added to your existing booking.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendDialog({ open: false, booking: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleExtendBooking} 
              disabled={submittingExtend} 
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-extend-btn"
            >
              {submittingExtend ? 'Extending...' : 'Extend Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverDashboard;
